import { Box, Divider, Grid, Pagination } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useMemo, useState } from 'react';
import ProfileSectionHeader from 'components/common/ProfileSectionHeader/ProfileSectionHeader';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import { useGetReports } from 'hooks/useCases';
import { useAuth } from 'context/AuthContext';
import ExportedItem from '../ExportedItem/ExportedItem';
import ExportedItemSkeleton from '../ExportedItemSkeleton/ExportedItemSkeleton';
import CustomText from 'components/common/CustomText/CustomText';
import { hexWithOpacity } from 'common/utils';

export default function ExportedProcedures() {
  const { user } = useAuth();
  const globalStyles = useGlobalStyles();

  const [page, setPage] = useState<number>(1);
  const pageLimit = 12;

  const {
    data: reports = [],
    isLoading,
  } = useGetReports(
    {
      userId: user?.id,
      page: 1,
      pageLimit: 1000,
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const total = reports.length;

  const numOfPages = useMemo(() => {
    return total > 0 ? Math.ceil(total / pageLimit) : 1;
  }, [total]);

  const paginatedReports = useMemo(() => {
    const startIndex = (page - 1) * pageLimit;
    const endIndex = startIndex + pageLimit;
    return reports.slice(startIndex, endIndex);
  }, [reports, page, pageLimit]);

  return (
    <Box
      className={globalStyles.sectionContainer}
      sx={{
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box px={2}>
          <ProfileSectionHeader
            title="Exported Procedures"
            icon={
              <AssignmentIcon
                sx={{ fontSize: 20, color: colors.primary }}
              />
            }
            buttons={[]}
          />
        </Box>

        <Divider />

      <Grid
        container
        spacing={1.5}
        p={2}
        sx={{
          flexGrow: isLoading || paginatedReports.length > 0 ? 0 : 1,
          display: 'flex',
        }}
      >
          {isLoading ? (
            Array.from({ length: pageLimit }).map((_, index) => (
              <Grid key={index} size={{ xs: 12, lg: 6, xl: 4 }}>
                {/* <ExportedItemSkeleton /> */}
              </Grid>
            ))
          ) : paginatedReports.length > 0 ? (
            paginatedReports.map((item: any) => (
              <Grid key={item.id} size={{ xs: 12, lg: 6, xl: 4 }}>
                <ExportedItem item={item} />
              </Grid>
            ))
          ) : (
            <Grid
              size={{ xs: 12 }}
              sx={{
                display: 'flex',
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                flex={1}
                textAlign="center"
                gap={2}
              >
                <Box height={100}>
                  <AssignmentIcon
                    sx={{ fontSize: 100, color: hexWithOpacity(colors.secondary, 0.5) }}
                  />
                </Box>
                <CustomText
                  text='No exported procedures found'
                  color={colors.secondary}
                />
              </Box>
            </Grid>
          )}
        </Grid>

      </Box>

      {numOfPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            p: 2,
            mt: 'auto',
            mb: 1,
          }}
        >
          <Pagination
            page={page}
            count={numOfPages}
            color="primary"
            onChange={(_, value) => setPage(value)}
            sx={{
              '& .MuiPaginationItem-root.Mui-selected:hover': {
                backgroundColor: 'primary.main',
                color: '#fff',
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
